import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const CoursesSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [expandedCard, setExpandedCard] = useState(null);

const courses = [
  {
    id: 1,
    title: "App Development with Flutter",
    subtitle: "iOS & Android",
    icon: "Smartphone",
    duration: "3 months",
    level: "Course + Internship",
    nextBatch: "1st Oct 2025",
    price: "₹4,999",
    originalPrice: "₹14,999",
    image: "/assets/images/app.png",
    description:
      "Master mobile app development with Flutter, the leading cross-platform framework. Build real-world apps, publish them on stores, and get career-ready skills companies demand.",
    curriculum: [
      "Flutter fundamentals & advanced concepts",
      "Dart programming language essentials",
      "App UI/UX design and state management",
      "API integration & database management",
      "Firebase authentication & push notifications",
      "Play Store & App Store deployment",
      "Payment gateway & monetization strategies"
    ],
    mentor: {
      name: "M Prashanth",
      role: "Senior Mobile App Developer",
      experience: "4+ years",
      avatar: "/assets/images/prashanth.jpg"
    },
    outcomes: [
      "Gain hands-on experience with Flutter",
      "Get knowledge on firebase",
      "Build and publish 3-5 production-ready mobile apps"
    ],
    availability: "8 seats left"
  },
  {
    id: 2,
    title: "Machine Learning & AI Development",
    subtitle: "ML, DL, NLP & MLOps",
    icon: "BrainCircuit",
    duration: "3 months",
    level: "Course + Internship",
    nextBatch: "1st Oct 2025",
    price: "₹4,499",
    originalPrice: "₹15,999",
    image: "/assets/images/ML.png",
    description:
      "Master Machine Learning and Artificial Intelligence through hands-on projects and real-world datasets. Learn to build, train, and deploy AI systems that solve real problems.",
    curriculum: [
      "Python for Data Science & ML",
      "Data preprocessing & visualization",
      "Supervised & Unsupervised algorithms",
      "Deep Learning with TensorFlow & PyTorch",
      "Computer Vision & NLP applications",
      "Model evaluation & hyperparameter tuning",
      "MLOps & model deployment to production"
    ],
    mentor: {
      name: "Dr. Kavya Menon",
      role: "AI Researcher at Google AI",
      experience: "10+ years",
      avatar: "https://randomuser.me/api/portraits/women/45.jpg"
    },
    outcomes: [
      "Get hands-on experience with Machine Learning projects",
      "Build and deploy 5+ ML/AI projects",
    ],
    availability: "6 seats left"
  }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    const element = document.getElementById('courses');
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const handleEnrollNow = (courseTitle) => {
    const element = document.getElementById('apply-section');
    if (element) {
      const headerHeight = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerHeight;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  const handleCardClick = (courseId) => {
    setExpandedCard(expandedCard === courseId ? null : courseId);
  };

  return (
    <section id="courses" className="py-20 bg-gradient-to-b from-surface to-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-text-primary mb-6">
            <span className="text-nipix-blue">I</span>
            <span className="text-text-primary">ndustry </span>
            <span className="text-nipix-blue">F</span>
            <span className="text-text-primary">ocused </span>
            <span className="text-nipix-blue">T</span>
            <span className="text-text-primary">echnical </span>            
            <span className="text-nipix-blue">C</span>
            <span className="text-text-primary">ourses</span>
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto leading-relaxed">
            Master cutting-edge technologies through hands-on projects, industry mentorship, and certificate internships that guarantee career success.
          </p>
        </motion.div>

        {/* Courses Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8">
          {courses.map((course, index) => (
            <motion.div
              key={course.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className={`group relative bg-white rounded-2xl shadow-lg border border-border overflow-hidden transition-all duration-500 hover:shadow-2xl cursor-pointer ${
                expandedCard === course.id ? 'lg:col-span-2 lg:row-span-2' : ''
              }`}
              onClick={() => handleCardClick(course.id)}
            >
              {/* Course Image */}
              <div className="relative h-48 overflow-hidden">
                <Image
                  src={course.image}
                  alt={course.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                
                {/* Course Icon */}
                <div className="absolute top-4 left-4 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                  <Icon name={course.icon} size={24} color="white" strokeWidth={2} />
                </div>

                
                {/* Availability Badge 
                <div className="absolute top-4 right-4 bg-warning text-white px-3 py-1 rounded-full text-xs font-semibold">
                  {course.availability}
                </div>*/}

                {/* Course Title Overlay 
                <div className="absolute bottom-4 left-4 text-white">
                  <h3 className="text-xl font-bold">{course.title}</h3>
                  <p className="text-sm opacity-90">{course.subtitle}</p>
                </div>*/}
              </div>

              {/* Course Content */}
              
              <div className="p-6">
                {/* Course Meta */}
                
                <div className="flex items-center justify-between mb-4">
                  
                  <div className="flex items-center space-x-4 text-sm text-text-secondary">
                    
                    <div className="flex items-center space-x-1">
                      
                      <Icon name="Clock" size={16} strokeWidth={2} />
                      <span>{course.duration}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Icon name="BarChart" size={16} strokeWidth={2} />
                      <span>{course.level}</span>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <p className="text-text-secondary mb-4 line-clamp-3">
                  {course.description}
                </p>

                {/* Expand Hint (only when not expanded) */}
                {expandedCard !== course.id && (
                  <div className="flex items-center gap-2 mt-2 mb-2 px-3 py-1 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-lg shadow-sm cursor-pointer group-hover:bg-primary/20 transition-all duration-200 select-none">
                    <svg className="w-5 h-5 text-primary animate-bounce" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                    <span className="text-xs text-primary font-medium">Tap or click to expand for full curriculum & outcomes</span>
                  </div>
                )}
                {/* Expanded Content */}
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ 
                    opacity: expandedCard === course.id ? 1 : 0, 
                    height: expandedCard === course.id ? 'auto' : 0 
                  }}
                  transition={{ duration: 0.3 }}
                  className="overflow-visible"
                >
                  {expandedCard === course.id && (
                    <div className="space-y-6 mb-6 opacity-100 transform translate-y-0">
                      {/* Curriculum */}
                      <div>
                        <h4 className="font-semibold text-text-primary mb-3 flex items-center">
                          <Icon name="BookOpen" size={16} className="mr-2" strokeWidth={2} />
                          What You'll Learn
                        </h4>
                        <ul className="space-y-2">
                          {course.curriculum.map((item, idx) => (
                            <li key={idx} className="flex items-start space-x-2 text-sm text-text-secondary">
                              <Icon name="Check" size={14} className="text-success mt-0.5 flex-shrink-0" strokeWidth={2} />
                              <span className="opacity-100">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Mentor 
                      <div className="bg-surface rounded-xl p-4">
                        <h4 className="font-semibold text-text-primary mb-3 flex items-center">
                          <Icon name="User" size={16} className="mr-2" strokeWidth={2} />
                          Your Mentor
                        </h4>
                        <div className="flex items-center space-x-3">
                          <Image
                            src={course.mentor.avatar}
                            alt={course.mentor.name}
                            className="w-12 h-12 rounded-full object-cover"
                          />
                          <div className="opacity-100">
                            <div className="font-medium text-text-primary">{course.mentor.name}</div>
                            <div className="text-sm text-text-secondary">{course.mentor.role}</div>
                            <div className="text-xs text-primary">{course.mentor.experience}</div>
                          </div>
                        </div>
                      </div>*/}

                      {/* Outcomes */}
                      <div>
                        <h4 className="font-semibold text-text-primary mb-3 flex items-center">
                          <Icon name="Target" size={16} className="mr-2" strokeWidth={2} />
                          Career Outcomes
                        </h4>
                        <ul className="space-y-2">
                          {course.outcomes.map((outcome, idx) => (
                            <li key={idx} className="flex items-start space-x-2 text-sm text-text-secondary">
                              <Icon name="TrendingUp" size={14} className="text-success mt-0.5 flex-shrink-0" strokeWidth={2} />
                              <span className="opacity-100">{outcome}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </motion.div>

                {/* Pricing & CTA */}
                <div className="border-t border-border pt-4">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className="text-2xl font-bold text-primary">{course.price}</span>
                        <span className="text-sm text-text-secondary line-through">{course.originalPrice}</span>
                      </div>
                      <div className="text-xs text-text-secondary">Next batch: {course.nextBatch}</div>
                    </div>
                  </div>

                  <Button
                    variant="default"
                    fullWidth
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEnrollNow(course.title);
                    }}
                    iconName="ArrowRight"
                    iconPosition="right"
                    className="btn-primary"
                  >
                    Enroll Now
                  </Button>
                </div>
              </div>

              {/* Hover Glow Effect */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-primary/10 to-secondary/10 opacity-0 group-hover:opacity-0 transition-opacity duration-500 pointer-events-none" />
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA 
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="text-center mt-16"
        >
          <div className="bg-gradient-to-r from-primary to-secondary rounded-2xl p-8 text-white">
            <Icon name="Zap" size={48} className="mx-auto mb-4" strokeWidth={2} />
            <h3 className="text-2xl font-bold mb-4">Can't Decide Which Course?</h3>
            <p className="text-lg opacity-90 mb-6 max-w-2xl mx-auto">
              Get personalized course recommendations based on your career goals and current skill level. Our career counselors will guide you to the perfect program.
            </p>
            <Button
              variant="outline"
              size="lg"
              onClick={() => {
                const element = document.getElementById('apply-section');
                if (element) {
                  const headerHeight = 80;
                  const elementPosition = element.getBoundingClientRect().top;
                  const offsetPosition = elementPosition + window.pageYOffset - headerHeight;
                  window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                }
              }}
              className="border-2 border-white text-white px-8 py-3 rounded-full font-semibold hover:bg-white hover:text-primary transition-all duration-300"
            >
              Get Free Career Consultation
            </Button>
          </div>
        </motion.div>*/}
      </div>
    </section>
  );
};

export default CoursesSection;